import { Badge } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { Card } from '@/ui/Card';
import { BarraDeProgreso } from '@/components/BarraDeProgreso';
import { cn } from '@/lib/cn';
import { yaPaso, type Evento, type Modalidad, type TipoDeElemento } from './api';

/* ── Lista o calendario ────────────────────────────────────────────────── */

/**
 * Las dos formas de ver los eventos.
 *
 * Son **dos rutas** (`#eventos` y `#eventos-calendario`), no pestañas: cada una
 * sustituye la pantalla entera, así que van como enlaces con `aria-current`, el
 * mismo criterio que `components/BarraDelCentro.tsx`.
 *
 * El vanilla ya explicaba por qué no se funden en una sola: no muestran lo
 * mismo. El calendario suma fechas especiales, cumpleaños, aniversarios e hitos
 * de vida; la lista, solo eventos. Fundirlas perdería lo segundo o cambiaría lo
 * que significa «Eventos».
 */
export function ConmutadorDeVistas({ activa }: { activa: 'lista' | 'calendario' }) {
  const vistas = [
    { clave: 'lista' as const, href: '#eventos', icono: 'bi-list-ul', texto: 'Lista' },
    { clave: 'calendario' as const, href: '#eventos-calendario', icono: 'bi-calendar3', texto: 'Calendario' },
  ];
  return (
    <nav aria-label="Cómo ver los eventos" className="mb-5">
      <ul className="inline-flex gap-1 rounded-sm border border-line bg-surface p-1">
        {vistas.map((v) => {
          const esActiva = v.clave === activa;
          return (
            <li key={v.clave}>
              <a
                href={v.href}
                aria-current={esActiva ? 'page' : undefined}
                className={cn(
                  'inline-flex min-h-9 items-center gap-2 rounded-xs px-3 text-2 font-semibold',
                  'transition-colors focus-visible:focus-anillo pointer-coarse:min-h-11',
                  esActiva ? 'bg-brand text-on-brand' : 'text-ink-2 hover:bg-sunken hover:text-brand-ink',
                )}
              >
                <i className={cn('bi', v.icono)} aria-hidden="true" />
                {v.texto}
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

/* ── Modalidad ─────────────────────────────────────────────────────────── */

/** La palabra, no la clave: el vanilla pintaba «hibrido», sin tilde. */
export const MODALIDADES: Record<Modalidad, { etiqueta: string; icono: string }> = {
  presencial: { etiqueta: 'Presencial', icono: 'bi-geo-alt' },
  virtual: { etiqueta: 'Virtual', icono: 'bi-camera-video' },
  hibrido: { etiqueta: 'Híbrido', icono: 'bi-broadcast' },
};

export function metaDeModalidad(m: string) {
  return MODALIDADES[m as Modalidad] ?? { etiqueta: m, icono: 'bi-calendar-event' };
}

/* ── Lo que cabe en el calendario ──────────────────────────────────────── */

/**
 * Icono, palabra y tinta de cada tipo de elemento del calendario.
 *
 * El vanilla escribía seis colores a mano por tema (`_SEM` y los encabezados
 * del detalle del día). Medido el 2026-09-17 con el píxel real, **cuatro de
 * los seis encabezados quedaban bajo 4,5 en cada tema**: «Aniversarios» 2,87 en
 * claro, «Nacimientos» 2,23 en oscuro. Aquí van las tintas `-ink` que ya se
 * midieron en Novedades y la Biblioteca, que se invierten solas en oscuro.
 *
 * Las fechas especiales no tienen tinta propia: su color lo elige gestión. Ver
 * `PuntoDeColor`.
 */
export const TIPOS_DEL_CALENDARIO: Record<
  TipoDeElemento,
  { icono: string; etiqueta: string; grupo: string; texto: string; tinte: string; punto: string }
> = {
  event: { icono: 'bi-calendar-event', etiqueta: 'Evento', grupo: 'Eventos', texto: 'text-info-ink', tinte: 'bg-info-ink/12', punto: 'bg-info-ink' },
  birthday: { icono: 'bi-cake2', etiqueta: 'Cumpleaños', grupo: 'Cumpleaños', texto: 'text-pink-ink', tinte: 'bg-pink-ink/12', punto: 'bg-pink-ink' },
  anniversary: { icono: 'bi-award', etiqueta: 'Aniversario laboral', grupo: 'Aniversarios laborales', texto: 'text-ok-ink', tinte: 'bg-ok-ink/12', punto: 'bg-ok-ink' },
  matrimonio: { icono: 'bi-gem', etiqueta: 'Matrimonio', grupo: 'Matrimonios', texto: 'text-purple-ink', tinte: 'bg-purple-ink/12', punto: 'bg-purple-ink' },
  nacimiento_hijo: { icono: 'bi-balloon', etiqueta: 'Nacimiento', grupo: 'Nacimientos', texto: 'text-teal-ink', tinte: 'bg-teal-ink/12', punto: 'bg-teal-ink' },
  special: { icono: 'bi-stars', etiqueta: 'Fecha especial', grupo: 'Fechas especiales', texto: 'text-ink-2', tinte: 'bg-sunken', punto: 'bg-ink-2' },
};

export const ORDEN_DEL_CALENDARIO: TipoDeElemento[] = [
  'event', 'special', 'birthday', 'anniversary', 'matrimonio', 'nacimiento_hijo',
];

/**
 * El color que eligió gestión para una fecha especial, como MARCA y no como
 * texto.
 *
 * El vanilla pintaba el nombre de la fecha en ese color sobre un tinte de ese
 * mismo color. Medido el 2026-09-17: con el navy por defecto, 11,28 en claro y
 * **1,03 en oscuro**; con un amarillo, 1,56 en claro. Cualquier color claro
 * fallaba en claro y cualquier oscuro en oscuro, y el arreglo para el modo
 * oscuro no llegaba a ejecutarse nunca: preguntaba por `window.Theme`, que no
 * existe porque `Theme` se declara con `const`.
 *
 * Así el texto va siempre en la tinta normal —que se lee en los dos temas— y el
 * color elegido se sigue viendo, en un punto con su filete.
 */
export function PuntoDeColor({ color, className }: { color?: string | null; className?: string }) {
  const valido = /^#[0-9a-f]{3,8}$/i.test(color ?? '') ? color! : undefined;
  return (
    <span
      aria-hidden="true"
      className={cn('inline-block size-2.5 shrink-0 rounded-full border border-line-strong', !valido && 'bg-ink-2', className)}
      style={valido ? { backgroundColor: valido } : undefined}
    />
  );
}

/* ── La tarjeta ────────────────────────────────────────────────────────── */

/**
 * Un evento en la lista.
 *
 * **Toda la tarjeta abre el detalle, con una sola parada de Tab.** El botón es
 * el título, y su `::after` se estira sobre la tarjeta entera; las acciones del
 * pie van por encima con `relative`. En el vanilla solo abría la portada.
 */
export function TarjetaDeEvento({
  e, onVer, onInscribirse, onCancelar, onReactivar, ocupado,
}: {
  e: Evento;
  onVer: (id: number) => void;
  onInscribirse: (id: number) => void;
  onCancelar: (e: Evento) => void;
  onReactivar?: (id: number) => void;
  ocupado: boolean;
}) {
  const modalidad = metaDeModalidad(e.modality);
  const paso = yaPaso(e.event_date);
  const lleno = e.max_capacity != null && e.registered_count >= e.max_capacity;

  return (
    <Card interactiva className={cn('relative flex w-full flex-col overflow-hidden', !e.is_active && 'opacity-75')}>
      <div className="flex h-36 shrink-0 items-center justify-center overflow-hidden border-b border-line bg-sunken">
        {e.cover_url ? (
          // `alt=""`: repite el título de debajo.
          <img src={e.cover_url} alt="" loading="lazy" className="size-full object-cover" />
        ) : (
          <i className={cn('bi text-9 text-muted opacity-60', modalidad.icono)} aria-hidden="true" />
        )}
      </div>

      <div className="flex flex-1 flex-col gap-2 p-4">
        {/* Dos cosas del botón del título, y ninguna es de adorno:
            · El anillo de foco va en el propio botón, no en el `::after`: apagar su
              contorno con `focus-visible:outline-none` para dibujarlo en la tarjeta
              es la trampa de React.md que deja el foco sin verse.
            · `text-inherit`, porque la hoja se importa SIN `preflight` y un
              `<button>` se queda con el color del navegador en vez de heredar el del
              título. En claro es negro sobre blanco y no se nota; en oscuro, medido
              el 2026-09-17, daba 1,44 de contraste —invisible— frente a los 6,66 del
              título de al lado. */}
        <h3 className="text-4 font-bold leading-snug text-brand-ink [overflow-wrap:anywhere]">
          {e.is_active ? (
            <button
              type="button"
              onClick={() => onVer(e.id)}
              className="rounded-xs text-left text-inherit after:absolute after:inset-0 focus-visible:focus-anillo"
            >
              {e.title}
            </button>
          ) : (
            e.title
          )}
        </h3>

        <ul className="flex flex-col gap-1 text-2 text-muted">
          <li className="flex items-center gap-1.5">
            <i className="bi bi-calendar-event" aria-hidden="true" />
            {Utils.fmtDatetime(e.event_date)}
          </li>
          {e.location && (
            <li className="flex items-center gap-1.5 [overflow-wrap:anywhere]">
              <i className="bi bi-geo-alt" aria-hidden="true" /> {e.location}
            </li>
          )}
          <li className="flex items-center gap-1.5">
            <i className={cn('bi', modalidad.icono)} aria-hidden="true" /> {modalidad.etiqueta}
          </li>
        </ul>

        {e.max_capacity != null ? (
          <div className="mt-auto flex flex-col gap-1.5 pt-1">
            <p className="text-2 text-ink-2">
              {e.registered_count} de {e.max_capacity} cupos ocupados
            </p>
            <BarraDeProgreso pct={(e.registered_count / e.max_capacity) * 100} etiqueta="Cupos ocupados" />
          </div>
        ) : (
          <p className="mt-auto pt-1 text-2 text-muted">Sin límite de cupos</p>
        )}
      </div>

      <div className="relative flex min-h-14 shrink-0 flex-wrap items-center justify-between gap-2 border-t border-line px-4 py-2.5">
        {!e.is_active ? (
          <>
            <Badge tone="neutral">Inactivo</Badge>
            {onReactivar && (
              <Button variant="secondary" size="sm" loading={ocupado} onClick={() => onReactivar(e.id)}>
                <i className="bi bi-arrow-counterclockwise" aria-hidden="true" /> Reactivar
              </Button>
            )}
          </>
        ) : e.is_registered ? (
          <>
            <Badge tone="ok"><i className="bi bi-check-lg" aria-hidden="true" /> Inscrito</Badge>
            {!paso && (
              <Button variant="ghost" size="sm" onClick={() => onCancelar(e)}>
                Cancelar inscripción
              </Button>
            )}
          </>
        ) : paso ? (
          <span className="text-2 text-muted">Ya pasó</span>
        ) : lleno ? (
          <Badge tone="warn">Cupos agotados</Badge>
        ) : (
          <Button variant="secondary" size="sm" loading={ocupado} onClick={() => onInscribirse(e.id)}>
            Inscribirse
          </Button>
        )}
      </div>
    </Card>
  );
}
